import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  SpaceEvent,
  SpaceEventsOverview,
  CreateSpaceEventRequest,
  Employer,
  HostableExpert,
  Invite,
  InviteLink,
  SessionAttendance,
  UpdateSessionRequest,
  UpdateSpaceEventRequest,
} from "@src/models/spaces";
import {
  cancelEvent,
  cancelSession,
  createEvent,
  createInviteLink,
  createInvites,
  getEmployers,
  getEvent,
  getEvents,
  getHostableExperts,
  getInviteLink,
  getInvites,
  getSessionAttendance,
  revokeInviteLink,
  updateEvent,
  updateSession,
} from "@src/services/spaces.service";

export const spacesKeys = {
  employers: ["spaces", "employers"] as const,
  experts: ["spaces", "experts"] as const,
  events: ["spaces", "events"] as const,
  event: (id: string) => ["spaces", "events", id] as const,
  invites: (sessionId: string) =>
    ["spaces", "sessions", sessionId, "invites"] as const,
  inviteLink: (sessionId: string) =>
    ["spaces", "sessions", sessionId, "invite-link"] as const,
  attendance: (sessionId: string) =>
    ["spaces", "sessions", sessionId, "attendance"] as const,
};

export const useEmployers = (): UseQueryResult<Employer[]> =>
  useQuery({ queryKey: spacesKeys.employers, queryFn: getEmployers });

export const useHostableExperts = (): UseQueryResult<HostableExpert[]> =>
  useQuery({ queryKey: spacesKeys.experts, queryFn: getHostableExperts });

export const useEvents = (): UseQueryResult<SpaceEventsOverview> =>
  useQuery({ queryKey: spacesKeys.events, queryFn: getEvents });

export const useEvent = (id: string): UseQueryResult<SpaceEvent> =>
  useQuery({
    queryKey: spacesKeys.event(id),
    queryFn: () => getEvent(id),
    enabled: Boolean(id),
  });

export const useCreateEventMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateSpaceEventRequest) => createEvent(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spacesKeys.events });
    },
  });
};

export const useUpdateEventMutation = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UpdateSpaceEventRequest) =>
      updateEvent(eventId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spacesKeys.event(eventId) });
      queryClient.invalidateQueries({ queryKey: spacesKeys.events });
    },
  });
};

export const useCancelEventMutation = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string | null) => cancelEvent(eventId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spacesKeys.event(eventId) });
      queryClient.invalidateQueries({ queryKey: spacesKeys.events });
    },
  });
};

export const useCancelSessionMutation = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      reason,
    }: {
      sessionId: string;
      reason: string | null;
    }) => cancelSession(sessionId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spacesKeys.event(eventId) });
      queryClient.invalidateQueries({ queryKey: spacesKeys.events });
    },
  });
};

export const useUpdateSessionMutation = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      request,
    }: {
      sessionId: string;
      request: UpdateSessionRequest;
    }) => updateSession(sessionId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spacesKeys.event(eventId) });
      queryClient.invalidateQueries({ queryKey: spacesKeys.events });
    },
  });
};

export const useInvites = (sessionId: string): UseQueryResult<Invite[]> =>
  useQuery({
    queryKey: spacesKeys.invites(sessionId),
    queryFn: () => getInvites(sessionId),
    enabled: Boolean(sessionId),
  });

export const useSessionAttendance = (
  sessionId: string,
): UseQueryResult<SessionAttendance> =>
  useQuery({
    queryKey: spacesKeys.attendance(sessionId),
    queryFn: () => getSessionAttendance(sessionId),
    enabled: Boolean(sessionId),
  });

export const useCreateInvitesMutation = (sessionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (emails: string[]) => createInvites(sessionId, emails),
    onSuccess: (invites) => {
      queryClient.setQueryData(spacesKeys.invites(sessionId), invites);
    },
  });
};

export const useInviteLink = (
  sessionId: string,
): UseQueryResult<InviteLink | null> =>
  useQuery({
    queryKey: spacesKeys.inviteLink(sessionId),
    queryFn: () => getInviteLink(sessionId),
    enabled: Boolean(sessionId),
  });

export const useCreateInviteLinkMutation = (sessionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (seatCap: number | null) =>
      createInviteLink(sessionId, seatCap),
    onSuccess: (link) => {
      queryClient.setQueryData(spacesKeys.inviteLink(sessionId), link);
    },
  });
};

export const useRevokeInviteLinkMutation = (sessionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => revokeInviteLink(sessionId),
    onSuccess: () => {
      queryClient.setQueryData(spacesKeys.inviteLink(sessionId), null);
    },
  });
};
